import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CalendarCheck, Check, RefreshCw } from 'lucide-react'
import { Button, Skeleton } from '../../core/index.js'
import type { AdminClientSummary } from '../../clients/index.js'
import type { AdminFacility } from '../index.js'
import { schedulingRulesOf, unitNamesFromConfig, type FacilityConfig } from '../index.js'
import { ApiError, Modal } from '../../core/index.js'
import { FacilityWorkersModal } from '../../roster/index.js'
import { KpiTile, deltaLabel } from '../index.js'
import { TierDistributionBars } from '../components/TierDistributionBars.js'
import { ScoreRunResult } from '../../scoring/index.js'
import { PeriodCloseResult } from '../../scoring/index.js'
import { DetailFields, DetailSection, QueryBlock, formatDate } from '../../core/index.js'
import { FacilityRecordEditor } from './FacilityRecordEditor.js'
import { formatAddress } from '../components/facilityAddress.js'
import { AdminFacilityDeliverySection, AdminFacilityMessagesSection, BarrierList } from '../../messages/index.js'
import { FacilityActivityCard } from '../../messages/index.js'
import { BridgeCalloutList, CensusEvaluationList, CensusRequirementView, CensusTable, MonthHealthReadOnly, PublishShiftsCard, ScheduleBoardReadOnly, ScheduleRunReadOnly, censusRange, describeRequirementFigure, todayDate } from '../../schedule/index.js'
import type { AvailableShift, SchedulePeriodView } from '../../schedule/index.js'
import type { FacilityActivityApi } from '../../messages/index.js'
import { useFeatures } from '../../core/index.js'
import type { ComponentProps } from 'react'
import type { AdminClient } from '../../core/index.js'
import type { FacilitiesAdminApi } from '../adminApi.js'
import type { BehaviorsAdminApi } from '../../behaviors/index.js'
import type { MessagesAdminApi } from '../../messages/index.js'
import type { RosterAdminApi } from '../../roster/index.js'
import type { ScheduleAdminApi } from '../../schedule/index.js'
import type { ScoringAdminApi } from '../../scoring/index.js'

/** The admin calls this screen makes itself, and what the screens it opens need. */
type AdminApi = FacilitiesAdminApi & RosterAdminApi & ScoringAdminApi & ScheduleAdminApi & MessagesAdminApi & BehaviorsAdminApi & { readonly client: AdminClient } & ComponentProps<typeof FacilityWorkersModal>['api']


interface FacilityDetailModalProps {
  api: AdminApi
  facility: AdminFacility
  client: AdminClientSummary
  /** Names of the facility administrators assigned to it. */
  administratorNames: readonly string[]
  onClose: () => void
}

const NOT_CONFIGURED = 'This facility has not been configured yet.'

/**
 * One facility in full: who runs it, its dashboard figures and tier spread,
 * what it measures, how it is configured, and what is on its calendar - with
 * its roster one click further in.
 */
export function FacilityDetailModal({ api, facility: opened, client, administratorNames, onClose }: FacilityDetailModalProps) {
  const [isViewingRoster, setIsViewingRoster] = useState(false)
  const features = useFeatures()
  // The row this was opened from is a snapshot; a save here updates what the dialog shows at once.
  const [facility, setFacility] = useState(opened)
  const [isEditingRecord, setIsEditingRecord] = useState(false)
  const { clientId, facilityId } = facility
  const key = (part: string) => ['admin', 'facility-detail', clientId, facilityId, part]

  const queryClient = useQueryClient()
  // Every admin query about this client may show a score, so all of them refetch after a run.
  const recalculate = useMutation({ mutationFn: () => api.recalculateFacilityScores(clientId, facilityId), onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin'] }) })
  const closePeriod = useMutation({ mutationFn: () => api.closeFacilityPeriod(clientId, facilityId, {}), onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin'] }) })
  const summaryQuery = useQuery({ queryKey: key('summary'), queryFn: () => api.facilityDashboardSummary(clientId, facilityId) })
  const tiersQuery = useQuery({ queryKey: key('tiers'), queryFn: () => api.facilityTierDistribution(clientId, facilityId) })
  const configQuery = useQuery({ queryKey: key('config'), queryFn: () => api.facilityConfig(clientId, facilityId) })
  const holidaysQuery = useQuery({ queryKey: key('holidays'), queryFn: () => api.facilityHolidayEvents(clientId, facilityId) })
  const shiftsQuery = useQuery({ queryKey: key('shifts'), queryFn: () => api.facilityAvailableShifts(clientId, facilityId) })
  const calloutsQuery = useQuery({ queryKey: key('bridge-callouts'), queryFn: () => api.facilityBridgeCallouts(clientId, facilityId) })
  const schedulePeriodQuery = useQuery({ queryKey: key('schedule-period'), queryFn: () => api.facilitySchedulePeriod(clientId, facilityId) })
  const monthHealthQuery = useQuery({ queryKey: key('month-health'), queryFn: () => api.facilityMonthHealth(clientId, facilityId) })
  const scheduleBoardQuery = useQuery({ queryKey: key('schedule-board'), queryFn: () => api.facilityScheduleBoard(clientId, facilityId), retry: false })
  const scheduleCoverageQuery = useQuery({ queryKey: key('schedule-coverage'), queryFn: () => api.facilityScheduleCoverage(clientId, facilityId), retry: false })
  // The census two weeks back and four ahead, and what today requires, read with the admin's mirrors (census response plan F0).
  const censusToday = todayDate()
  const censusQuery = useQuery({ queryKey: key('census'), queryFn: () => api.facilityCensus(clientId, facilityId, censusRange(censusToday)) })
  const censusRequirementsQuery = useQuery({ queryKey: key('census-requirements'), queryFn: () => api.facilityCensusRequirements(clientId, facilityId, { date: censusToday }) })
  const censusEvaluationsQuery = useQuery({ queryKey: key('census-evaluations'), queryFn: () => api.facilityCensusEvaluations(clientId, facilityId, censusToday.slice(0, 7)) })
  const barriersQuery = useQuery({ queryKey: key('barriers'), queryFn: () => api.facilityBarriers(clientId, facilityId) })
  // The dashboard's Recent Activity, read with the admin's credential: it starts 7 days back, since the admin has no sign-in at the client.
  const activityApi: FacilityActivityApi = { queryKey: key('activity'), endpoint: '…/activity', load: (request) => api.facilityActivity(clientId, facilityId, request) }
  // Only to name each note's worker; the roster dialog reads the same list, so it is usually cached.
  const rosterQuery = useQuery({ queryKey: ['admin', 'facility-workers', clientId, facilityId], queryFn: () => api.listFacilityWorkers(clientId, facilityId) })
  const rosterNames = new Map((rosterQuery.data ?? []).map((worker) => [worker.workerId, worker.name]))
  // Shares the cache with the Behaviors tab, so a change made there shows here.
  const behaviorsApi = api.behaviorsFor(clientId)
  const behaviorsQuery = useQuery({ queryKey: behaviorsApi.queryKey, queryFn: () => behaviorsApi.load() })
  const workerCount = facility.workerCount ?? 0
  const hasWorkers = workerCount > 0

  return (
    <>
      <Modal
        eyebrow="Facility"
        title={facility.name}
        subtitle={client.organizationName}
        // With the roster open on top, Escape and backdrop clicks belong to the roster.
        canClose={!isViewingRoster}
        onClose={onClose}
        footer={
          <div className="flex items-center gap-4">
            <Button icon={Check} autoFocus onClick={onClose}>
              Close
            </Button>
            <button type="button" onClick={() => setIsViewingRoster(true)} className="text-xs font-bold text-marine hover:text-marine-dk">
              View the {workerCount.toLocaleString()} {workerCount === 1 ? 'worker' : 'workers'} on its roster
            </button>
          </div>
        }
      >
        <div className="flex flex-col gap-6 bg-canvas p-6">
          <DetailSection title="Facility" endpoint="…/facilities/{facilityId}">
            <div className="flex flex-col gap-3">
              <DetailFields
                fields={[
                  { label: 'Client', value: client.organizationName },
                  { label: 'Registered', value: formatDate(facility.createdAt) },
                  { label: 'Time zone', value: facility.timezone ?? 'UTC (not set)' },
                  // The operator's alone to set: shown here, never edited here.
                  { label: 'Address', value: facility.address ? formatAddress(facility.address) : 'Not set - the operator sets this from their Configuration page' },
                  { label: 'Phone', value: facility.phone ?? 'Not set - the operator sets this from their Configuration page' },
                  { label: 'Administrators', value: administratorNames.length === 0 ? 'None assigned' : administratorNames.join(', ') },
                ]}
              />
              {isEditingRecord ? (
                <FacilityRecordEditor
                  api={api}
                  facility={facility}
                  onSaved={(updated) => {
                    setFacility((previous) => ({ ...previous, ...updated }))
                    setIsEditingRecord(false)
                  }}
                  onCancel={() => setIsEditingRecord(false)}
                />
              ) : (
                <div>
                  <button type="button" onClick={() => setIsEditingRecord(true)} className="text-xs font-bold text-marine hover:text-marine-dk">
                    Edit name and time zone
                  </button>
                </div>
              )}
            </div>
          </DetailSection>

          <DetailSection title="Dashboard" endpoint="…/dashboard/summary">
            <QueryBlock query={summaryQuery} missing={NOT_CONFIGURED}>
              {(summary) => (
                <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                  <KpiTile label="Active Employees" value={String(summary.activeEmployees)} sub="On platform" />
                  <KpiTile label="Gold Tier" value={String(summary.goldTierCount)} sub={`${summary.goldTierPct}% of workforce`} accent="border-t-gold" />
                  <KpiTile label="Avg Reliability Score" value={String(summary.avgReliabilityScore)} sub={deltaLabel(summary.avgReliabilityScoreDeltaPoints, ' pts', 'vs. last month')} accent="border-t-coral" />
                  <KpiTile label="Filled From the Roster" value={summary.shiftsViaInternalBidPct === null ? '—' : `${summary.shiftsViaInternalBidPct}%`} sub={summary.shiftsViaInternalBidPct === null ? 'This month, once released' : 'This month'} />
                  <KpiTile label="Call-Outs This Month" value={String(summary.callOutsThisMonth)} sub={deltaLabel(summary.callOutsDeltaPct, '%', 'vs. prior month')} accent="border-t-coral" />
                  <KpiTile label="Open Seats Left" value={summary.openGapsRemaining === null ? '—' : String(summary.openGapsRemaining)} sub={summary.openGapsRemaining === null ? 'This month, once released' : 'This month, from today'} />
                  <div className="col-span-2 text-xs text-ink-mid md:col-span-3">
                    <span className="font-bold text-ink">{(summary.administrators ?? []).length === 1 ? 'Administrator: ' : 'Administrators: '}</span>
                    {(summary.administrators ?? []).length === 0
                      ? 'none yet'
                      : (summary.administrators ?? []).map((administrator) => `${administrator.name}${administrator.invited ? ' (invited)' : ''} · ${administrator.email}${administrator.phone ? ` · ${administrator.phone}` : ''}`).join('; ')}
                  </div>
                </div>
              )}
            </QueryBlock>
          </DetailSection>

          <DetailSection title="Scores" endpoint="…/scores/recalculate">
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-[11px] text-ink-mid max-w-xl">Recomputes every worker's running score for this period from their outcomes, with the behaviors and rules as configured now. Tiers do not change until the period closes.</p>
                <Button icon={RefreshCw} disabled={recalculate.isPending || !hasWorkers} title={hasWorkers ? undefined : 'No workers on the roster yet'} onClick={() => recalculate.mutate()}>
                  {recalculate.isPending ? 'Recalculating…' : 'Recalculate scores'}
                </Button>
              </div>
              {!hasWorkers && <p className="text-[11px] text-ink-mid">No workers on the roster yet, so there is nothing to score or close.</p>}
              {recalculate.isError && <p className="text-xs font-bold text-coral">{recalculate.error instanceof Error && recalculate.error.message ? recalculate.error.message : 'The recalculation failed.'}</p>}
              {recalculate.data && <ScoreRunResult run={recalculate.data} />}
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-marine/10 pt-3">
                <p className="text-[11px] text-ink-mid max-w-xl">Closes the last period that has ended: a final per worker, tiers set from the finals under the facility's rules, and the next period's bid windows. Recorded as closed by the platform admin.</p>
                <Button icon={CalendarCheck} variant="secondary" disabled={closePeriod.isPending || !hasWorkers} title={hasWorkers ? undefined : 'No workers on the roster yet'} onClick={() => closePeriod.mutate()}>
                  {closePeriod.isPending ? 'Closing…' : 'Close last period'}
                </Button>
              </div>
              {closePeriod.isError && <p className="text-xs font-bold text-coral">{closePeriod.error instanceof Error && closePeriod.error.message ? closePeriod.error.message : 'The close failed.'}</p>}
              {closePeriod.data && <PeriodCloseResult close={closePeriod.data} />}
            </div>
          </DetailSection>

          <DetailSection title="Tier distribution" endpoint="…/dashboard/tier-distribution">
            <QueryBlock query={tiersQuery} missing={NOT_CONFIGURED}>
              {(distribution) => <TierDistributionBars tiers={distribution.tiers} />}
            </QueryBlock>
          </DetailSection>

          <DetailSection title="What it measures" endpoint="…/behaviors">
            <QueryBlock query={behaviorsQuery} missing="No behaviors.">
              {(state) => {
                const effective = state.facilities.find((candidate) => candidate.facilityId === facilityId)?.effective ?? []
                return (
                  <ul className="divide-y divide-marine/10 border border-marine/15 bg-white text-xs">
                    {effective.map((behavior) => (
                      <li key={behavior.key} className="flex items-center justify-between gap-3 px-3 py-1.5">
                        <span className={behavior.enabled ? 'font-bold text-ink' : 'text-ink-mid line-through'}>{behavior.label}</span>
                        <span className="text-ink-mid">
                          {behavior.enabled ? `${behavior.weight}%` : 'Off'} · {behavior.source === 'override' ? 'this facility only' : 'follows master'}
                        </span>
                      </li>
                    ))}
                  </ul>
                )
              }}
            </QueryBlock>
          </DetailSection>

          <DetailSection title="Configuration" endpoint="…/config">
            <QueryBlock query={configQuery} missing={NOT_CONFIGURED}>
              {(config) => (
                <DetailFields
                  fields={[
                    { label: 'Gold from', value: config.tierThresholds.goldMinScore },
                    { label: 'Silver from', value: config.tierThresholds.silverMinScore },
                    { label: 'Bronze from', value: config.tierThresholds.bronzeMinScore },
                    { label: 'Tiebreaker', value: config.tierThresholds.tiebreaker },
                    { label: 'Scoring period closes', value: config.bidWindowTiming.scoringPeriodCloses },
                    { label: 'Gold window opens', value: config.bidWindowTiming.goldWindowOpensAt },
                    { label: 'Clinical Bridge', value: config.clinicalBridge.enabled ? `On · ${config.clinicalBridge.responseWindowMinutes} min response · ${config.clinicalBridge.arrivalWindowMinutes ?? 60} min arrival` : 'Off' },
                    { label: 'Partner prioritization', value: (config.prioritizationEnabled ?? true) ? 'On' : 'Off' },
                    { label: 'Scheduling', value: describeScheduling(config) },
                  ]}
                />
              )}
            </QueryBlock>
          </DetailSection>

          <DetailSection title="Publish shifts" endpoint="…/import/shifts">
            {configQuery.data && shiftsQuery.data ? (
              <PublishShiftsCard
                rules={schedulingRulesOf(configQuery.data)}
                existing={shiftsQuery.data}
                endpoint="POST /admin/clients/{clientId}/facilities/{facilityId}/import/shifts"
                publish={async (part) => {
                  const written = await api.facilityPublishShifts(clientId, facilityId, part)
                  void queryClient.invalidateQueries({ queryKey: key('shifts') })
                  void queryClient.invalidateQueries({ queryKey: key('schedule-period') })
                  return written
                }}
              />
            ) : (
              <p className="text-xs text-ink-mid">{configQuery.isError || shiftsQuery.isError ? NOT_CONFIGURED : 'Loading…'}</p>
            )}
          </DetailSection>

          <DetailSection title="The month's health" endpoint="…/schedule/health">
            <QueryBlock query={monthHealthQuery} missing="This facility no longer exists.">
              {(view) => (
                <div className="flex flex-col gap-2">
                  <p className="text-[11px] text-ink-mid">What the facility’s dashboard warns about the month being built - the census, the workers available, the units - with the verdict the release guard reads. Read-only here; the buttons are the facility’s.</p>
                  <MonthHealthReadOnly view={view} />
                </div>
              )}
            </QueryBlock>
          </DetailSection>

          <DetailSection title="The month's schedule run" endpoint="…/schedule/period">
            <QueryBlock query={schedulePeriodQuery} missing="This facility no longer exists.">
              {(view) => <AdminScheduleRun api={api} clientId={clientId} facilityId={facilityId} view={view} shifts={shiftsQuery.data ?? null} />}
            </QueryBlock>
          </DetailSection>

          <AdminFacilityDeliverySection api={api} clientId={clientId} facilityId={facilityId} />

          <DetailSection title="Census" endpoint="…/census">
            <QueryBlock query={censusQuery} missing="This facility no longer exists.">
              {(view) => (
                <div className="flex flex-col gap-3">
                  <p className="text-[11px] text-ink-mid">The number of residents on each unit by day, as the facility administrator records it on the Dashboard’s Today tab’s Census sub-tab or the partner’s feed writes it; recorded counts in bold, forecasts in grey. Read-only here.</p>
                  {censusRequirementsQuery.data && censusRequirementsQuery.data.units.some((entry) => entry.requirement) && (
                    <ul className="divide-y divide-marine/10">
                      {censusRequirementsQuery.data.units.map(({ unit, figure, requirement }) => (
                        <li key={unit.id} className="py-1.5">
                          <div className="text-xs font-bold text-ink">
                            {unit.name} today <span className="font-normal text-ink-mid">· {figure ? `census ${figure.census}, ${describeRequirementFigure(figure, censusToday)}` : describeRequirementFigure(null, censusToday)}</span>
                          </div>
                          {requirement && <CensusRequirementView requirement={requirement} />}
                        </li>
                      ))}
                    </ul>
                  )}
                  <CensusTable view={view} today={censusToday} />
                  <div>
                    <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-marine">What the engine did this month</div>
                    {censusEvaluationsQuery.data ? <CensusEvaluationList records={censusEvaluationsQuery.data} canDecide={false} facilityId={facilityId} /> : <p className="text-[11px] text-ink-mid">{censusEvaluationsQuery.isError ? 'The evaluations could not be loaded.' : 'Loading…'}</p>}
                  </div>
                </div>
              )}
            </QueryBlock>
          </DetailSection>

          <DetailSection title="After the release: the schedule board" endpoint="…/schedule/board">
            {scheduleBoardQuery.isPending ? <Skeleton /> : scheduleBoardQuery.isError ? <p className="text-xs text-ink-mid">{scheduleBoardQuery.error instanceof ApiError && scheduleBoardQuery.error.status === 409 ? 'The board opens with the month’s release; nothing is released yet.' : 'The board could not be loaded.'}</p> : <ScheduleBoardReadOnly board={scheduleBoardQuery.data} coverage={scheduleCoverageQuery.data ?? null} units={new Map((configQuery.data ? schedulingRulesOf(configQuery.data).units : []).map((unit) => [unit.id, unit.name]))} />}
          </DetailSection>

          <DetailSection title="Clinical Bridge callouts" endpoint="…/bridge/callouts">
            <QueryBlock query={calloutsQuery} missing="This facility no longer exists.">
              {(callouts) => <BridgeCalloutList callouts={callouts} empty="No callout has been opened at this facility." />}
            </QueryBlock>
          </DetailSection>

          <FacilityActivityCard api={activityApi} names={rosterNames} units={unitNamesFromConfig(configQuery.data)} />

          {/* Each add-on's read-only section (src/features/index.ts). */}
          {features.map((feature) => (feature.adminFacilitySection ? <feature.adminFacilitySection key={feature.key} client={api.client} clientId={clientId} facilityId={facilityId} /> : null))}

          <DetailSection title="What workers say is getting in the way" endpoint="…/barriers">
            <QueryBlock query={barriersQuery} missing="This facility no longer exists.">
              {(barriers) => (
                <BarrierList
                  barriers={barriers}
                  audience="staff"
                  names={rosterNames}
                  empty="Nothing yet. A worker writes here from their own app once a behavior is costing them points; the facility's staff mark each seen."
                />
              )}
            </QueryBlock>
          </DetailSection>

          <AdminFacilityMessagesSection api={api} clientId={clientId} facilityId={facilityId} />

          <div className="grid gap-6 md:grid-cols-2">
            <DetailSection title="Holiday events" endpoint="…/holiday-events">
              <QueryBlock query={holidaysQuery} missing={NOT_CONFIGURED}>
                {(events) =>
                  events.length === 0 ? (
                    <div className="text-xs text-ink-mid">None configured.</div>
                  ) : (
                    <ul className="divide-y divide-marine/10 border border-marine/15 bg-white text-xs">
                      {events.map((event) => (
                        <li key={event.id} className="px-3 py-1.5">
                          <div className="font-bold text-ink">{event.name}</div>
                          <div className="text-ink-mid">
                            {event.periodStart} to {event.periodEnd} · ${event.bonusAmount} for {event.shiftsRequiredForBonus} shifts
                          </div>
                        </li>
                      ))}
                    </ul>
                  )
                }
              </QueryBlock>
            </DetailSection>

            <DetailSection title="Open shifts" endpoint="…/schedule/available-shifts">
              <QueryBlock query={shiftsQuery} missing={NOT_CONFIGURED}>
                {(shifts) =>
                  shifts.length === 0 ? (
                    <div className="text-xs text-ink-mid">No shifts open for bidding.</div>
                  ) : (
                    <ul className="max-h-48 divide-y divide-marine/10 overflow-auto border border-marine/15 bg-white text-xs">
                      {shifts.map((shift) => (
                        <li key={shift.id} className="flex justify-between gap-3 px-3 py-1.5">
                          <span className="font-bold text-ink">
                            {shift.date} <span className="font-normal capitalize text-ink-mid">· {shift.period}</span>
                          </span>
                          <span className="text-ink-mid">
                            {shift.startTime}–{shift.endTime} · unit {shift.unitId}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )
                }
              </QueryBlock>
            </DetailSection>
          </div>
        </div>
      </Modal>
      {isViewingRoster && <FacilityWorkersModal api={api} facility={facility} backLabel="facility details" onClose={() => setIsViewingRoster(false)} />}
    </>
  )
}

/** "5 a week · Gold/Silver 128–160 h, Bronze 160 h · probationary assigned · floor refused" - the scheduling rules in a line. */
function describeScheduling(config: FacilityConfig): string {
  const rules = schedulingRulesOf(config)
  const hours = (tier: 'gold' | 'bronze'): string => {
    const cell = rules.limits[tier].full_time
    return cell.hoursMin === cell.hoursMax ? `${cell.hoursMin} h` : `${cell.hoursMin}–${cell.hoursMax} h`
  }
  const assigned = (['gold', 'silver', 'bronze', 'probationary'] as const).filter((tier) => !rules.tiersThatBid.includes(tier))
  return `${rules.maxShiftsPerWeek} a week · Gold ${hours('gold')}, Bronze ${hours('bronze')} full time · ${assigned.length ? `${assigned.join(', ')} assigned` : 'every tier bids'} · floor ${rules.contractualFloor === 'block' ? 'refused' : 'flagged'} · Bridge by ${rules.bridge.eligibility === 'staff' ? 'staff' : 'employment type'}${rules.units.length ? ` · ${rules.units.length} units` : ''}${rules.minimumStaffing.length ? ` · ${rules.minimumStaffing.length} staffing minimums` : ''}`
}

/** The released run where there is one, else the latest, read through the admin mirror. */
function AdminScheduleRun({ api, clientId, facilityId, view, shifts }: { api: Pick<ScheduleAdminApi, 'facilityScheduleRun' | 'facilitySchedulePeriodWorkers' | 'facilityShiftPreferences'>; clientId: string; facilityId: string; view: SchedulePeriodView; shifts?: AvailableShift[] | null }) {
  const shown = view.releasedRun ?? view.latestRun
  const runQuery = useQuery({ queryKey: ['admin', 'schedule-run', clientId, facilityId, shown?.runId ?? null], queryFn: () => api.facilityScheduleRun(clientId, facilityId, shown?.runId as string), enabled: shown !== null })
  // The month's readiness, read-only: who saved preferences and drafts, and what each shift type is asked for.
  const workersQuery = useQuery({ queryKey: ['admin', 'schedule-period-workers', clientId, facilityId, view.period], queryFn: () => api.facilitySchedulePeriodWorkers(clientId, facilityId, view.period) })
  const preferencesQuery = useQuery({ queryKey: ['admin', 'facility-shift-preferences', clientId, facilityId, view.period], queryFn: () => api.facilityShiftPreferences(clientId, facilityId, view.period) })
  return <ScheduleRunReadOnly view={view} run={runQuery.data ?? null} workers={workersQuery.data?.workers} shifts={shifts} preferences={preferencesQuery.data?.preferences ?? []} />
}
